import { useEffect, useCallback, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Skeleton,
  TextField,
  Pagination,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { fetchVideoStats } from "../utils/generalSubjectApi";
import {
  FaArrowLeft,
  FaPlay,
  FaFire,
  FaCoins,
  FaChartBar,
  FaTrophy,
} from "react-icons/fa";

/* ─── Font injection ─── */
const injectFonts = () => {
  const link = document.createElement("link");
  link.href =
    "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Sora:wght@700;800&display=swap";
  link.rel = "stylesheet";
  document.head.appendChild(link);
};

/* ─── Animated counter ─── */
function AnimatedNumber({ value, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState(0);
  const numericValue = parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const step = 16;
    const increment = numericValue / (duration / step);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setDisplay(numericValue);
        clearInterval(timer);
      } else setDisplay(Math.floor(start));
    }, step);
    return () => clearInterval(timer);
  }, [numericValue]);

  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

/* ─── Stat card ─── */
const StatCard = ({
  title,
  value,
  color,
  icon: Icon,
  delay = 0,
  prefix = "",
  suffix = "",
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ flex: 1, minWidth: 200 }}
  >
    <Box
      sx={{
        p: 3,
        borderRadius: "20px",
        background: "#fff",
        border: `1px solid ${color}20`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.04), 0 0 0 1px ${color}10`,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 16px 40px rgba(0,0,0,0.07), 0 0 0 1px ${color}20`,
        },
      }}
    >
      {/* Background glow */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${color}18 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={2}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "#64748b",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: `linear-gradient(135deg, ${color}20, ${color}10)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${color}25`,
          }}
        >
          <Icon size={15} color={color} />
        </Box>
      </Stack>
      <Typography
        sx={{
          fontSize: "2rem",
          fontWeight: 800,
          color,
          fontFamily: "'Sora', sans-serif",
          letterSpacing: "-1px",
          lineHeight: 1,
        }}
      >
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
      </Typography>
    </Box>
  </motion.div>
);

/* ─── Video bar row ─── */
const VideoRow = ({ vid, percent, rank, delay = 0 }) => {
  const [hovered, setHovered] = useState(false);
  const colors = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd"];
  const color = colors[Math.min(rank, colors.length - 1)];

  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          mb: 2,
          p: "16px 20px",
          borderRadius: "16px",
          background: hovered ? "#f8faff" : "#fff",
          border: `1px solid ${hovered ? color + "30" : "rgba(0,0,0,0.05)"}`,
          boxShadow: hovered
            ? `0 8px 24px rgba(99,102,241,0.1)`
            : "0 2px 8px rgba(0,0,0,0.03)",
          transition: "all 0.25s ease",
          cursor: "default",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* Rank badge */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                background:
                  rank === 0
                    ? "linear-gradient(135deg, #f59e0b, #fbbf24)"
                    : `linear-gradient(135deg, ${color}30, ${color}15)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border:
                  rank === 0 ? "1px solid #f59e0b40" : `1px solid ${color}25`,
              }}
            >
              {rank === 0 ? (
                <FaTrophy size={12} color="#f59e0b" />
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color,
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  {rank + 1}
                </Typography>
              )}
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "0.9rem",
                fontFamily: "'DM Sans', sans-serif",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {vid.title}
            </Typography>
          </Stack>
          <Box
            sx={{
              px: 1.4,
              py: 0.3,
              borderRadius: "20px",
              background: `${color}12`,
              border: `1px solid ${color}25`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              
              {Number(vid.watch_count || 0).toLocaleString()} views
            </Typography>
          </Box>
        </Stack>

        {/* Progress bar */}
        <Box
          sx={{
            height: 6,
            borderRadius: 10,
            background: "#f1f5f9",
            overflow: "hidden",
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{
              duration: 0.8,
              delay: delay + 0.2,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              height: "100%",
              background:
                rank === 0
                  ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                  : `linear-gradient(90deg, ${color}, ${
                      colors[Math.min(rank + 1, colors.length - 1)]
                    })`,
              borderRadius: 10,
            }}
          />
        </Box>
      </Box>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════ */
export default function VideoStatsPageAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthlyPage, setMonthlyPage] = useState(1);
  const [videoPage, setVideoPage] = useState(1);
  const [teacherPage, setTeacherPage] = useState(1);

  const itemsPerPage = 10;
  const navigate = useNavigate();
  const now = new Date();

  const [selectedDate, setSelectedDate] = useState(
    `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`
  );

  const fetchStatsData = useCallback(async () => {
    try {
      setLoading(true);

      const [year, month] = selectedDate.split("-");

      const data = await fetchVideoStats(year, month);
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchStatsData();
  }, [fetchStatsData]);
  
  useEffect(() => {
    document.title = "Video Statistics | Admin";
    injectFonts();
  }, []);

  // Reset pagination whenever the selected month changes
  useEffect(() => {
    setMonthlyPage(1);
    setVideoPage(1);
    setTeacherPage(1);
  }, [selectedDate]);

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(145deg, #f8f9ff, #f8fafc)",
          p: 4,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <TextField
          type="month"
          value={selectedDate}
          onChange={(e) =>
            setSelectedDate(e.target.value)
          }
          size="small"
          sx={{
            minWidth: 190,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              background: "#fff",
            },
          }}
        />
        <Skeleton
          variant="text"
          width={120}
          height={40}
          sx={{ borderRadius: 2, mb: 4 }}
        />
        <Skeleton
          variant="text"
          width={280}
          height={52}
          sx={{ borderRadius: 2, mb: 4 }}
        />
        <Stack direction="row" spacing={3} mb={4}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={110}
              sx={{ borderRadius: "20px", flex: 1 }}
            />
          ))}
        </Stack>
        <Skeleton
          variant="rounded"
          height={120}
          sx={{ borderRadius: "20px", mb: 4 }}
        />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={78}
            sx={{ borderRadius: "16px", mb: 2 }}
          />
        ))}
      </Box>
    );
  }

  // All videos, sorted by total watches
  const sortedVideos = [...(stats?.videos || [])].sort(
    (a, b) => Number(b.watch_count || 0) - Number(a.watch_count || 0)
  );

  // Top video is calculated from ALL videos
  const topVideo = sortedVideos[0];
  const maxWatch = Number(topVideo?.watch_count || 1);

  // Monthly performance videos
  const monthlyVideos = [...(stats?.videos || [])]
    .filter(video => Number(video.selected_watches || 0) > 0)
    .sort(
      (a, b) =>
        Number(b.selected_watches || 0) -
        Number(a.selected_watches || 0)
    );

  // Pagination calculations
  const monthlyPages = Math.ceil(monthlyVideos.length / itemsPerPage);
  const videoPages = Math.ceil(sortedVideos.length / itemsPerPage);

  const teachers = stats?.teachers || [];
  const teacherPages = Math.ceil(teachers.length / itemsPerPage);

  // Current page data
  const paginatedMonthlyVideos = monthlyVideos.slice(
    (monthlyPage - 1) * itemsPerPage,
    monthlyPage * itemsPerPage
  );

  const paginatedVideos = sortedVideos.slice(
    (videoPage - 1) * itemsPerPage,
    videoPage * itemsPerPage
  );

  const paginatedTeachers = teachers.slice(
    (teacherPage - 1) * itemsPerPage,
    teacherPage * itemsPerPage
  );
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background:
          "linear-gradient(145deg, #f8f9ff 0%, #f8fafc 60%, #f0f7ff 100%)",
        fontFamily: "'DM Sans', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Noise texture */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          opacity: 0.02,
          pointerEvents: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: 0,
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
          py: 4,
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: "#475569",
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateX(-2px)",
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <FaArrowLeft size={15} />
              </IconButton>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#94a3b8",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mb: 0.2,
                  }}
                >
                  Admin Dashboard
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.6rem",
                    color: "#0f172a",
                    letterSpacing: "-0.7px",
                    fontFamily: "'Sora', sans-serif",
                    lineHeight: 1,
                  }}
                >
                  Video Statistics
                </Typography>
              </Box>
            </Stack>

            {/* date  */}
           <TextField
              type="month"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="small"
              sx={{
                minWidth: 190,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "#fff",
                },
              }}
            />
          </Stack>
        </motion.div>

        {/* Stat cards */}
        <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap mb={4}>
          <StatCard
            title="Selected Month Revenue"
            value={stats?.selected?.total_revenue || 0}
            color="#10b981"
            icon={FaCoins}
            prefix="₦"
          />

          <StatCard
            title="Teacher 20% Share"
            value={stats?.selected?.teacher_revenue || 0}
            color="#8b5cf6"
            icon={FaChartBar}
            prefix="₦"
          />

          <StatCard
            title="Selected Year Revenue"
            value={stats?.year?.total_revenue || 0}
            color="#6366f1"
            icon={FaCoins}
            prefix="₦"
          />

          <StatCard
            title="Year Teacher Share"
            value={stats?.year?.teacher_revenue || 0}
            color="#f59e0b"
            icon={FaChartBar}
            prefix="₦"
          />
        </Stack>

        <Box
          sx={{
            background: "#fff",
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Video Performance
            </Typography>
          </Box>

            {paginatedMonthlyVideos.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No video watches recorded for this month.
                </Typography>
              </Box>
            ) : (
              paginatedMonthlyVideos.map((video) => (
                <Box
                  key={video.id}
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.88rem",
                      }}
                    >
                      {video.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: "#64748b",
                      }}
                    >
                      Teacher: {video.teacher || "No teacher"}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "#6366f1",
                      }}
                    >
                      {Number(video.selected_watches || 0).toLocaleString()} watches
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: "#10b981",
                        fontWeight: 700,
                      }}
                    >
                      ₦{Number(video.selected_revenue || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}

            {monthlyPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Pagination
                  count={monthlyPages}
                  page={monthlyPage}
                  onChange={(event, page) => setMonthlyPage(page)}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
        </Box>

        {/* Top video hero card */}
        {topVideo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.55,
              delay: 0.34,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <Box
              sx={{
                mb: 4,
                p: "28px 32px",
                borderRadius: "22px",
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",
                color: "#fff",
                position: "relative",
                overflow: "hidden",
                boxShadow:
                  "0 20px 48px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
            >
              {/* Inner glow */}
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -60,
                  left: "30%",
                  width: 300,
                  height: 300,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.08)",
                  pointerEvents: "none",
                }}
              />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                position="relative"
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        p: 0.6,
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                      }}
                    >
                      <FaFire size={13} color="#fbbf24" />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.73rem",
                        fontWeight: 700,
                        opacity: 0.75,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      Top Performing
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.3rem",
                      letterSpacing: "-0.5px",
                      mb: 0.5,
                      fontFamily: "'Sora', sans-serif",
                      maxWidth: 420,
                      lineHeight: 1.25,
                    }}
                  >
                    {topVideo.title}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                    <FaPlay size={10} style={{ opacity: 0.7 }} />
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        opacity: 0.8,
                        fontWeight: 600,
                      }}
                    >
                      {Number(topVideo.watch_count || 0).toLocaleString()} views
                    </Typography>
                  </Stack>
                </Box>

                {/* Big number */}
                <Box sx={{ textAlign: "right", opacity: 0.15 }}>
                  <Typography
                    sx={{
                      fontSize: "5rem",
                      fontWeight: 800,
                      fontFamily: "'Sora', sans-serif",
                      letterSpacing: "-4px",
                      lineHeight: 1,
                    }}
                  >
                    #1
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </motion.div>
        )}

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.42 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
            <Box
              sx={{
                width: 3,
                height: 20,
                borderRadius: 2,
                background: "linear-gradient(180deg, #6366f1, #8b5cf6)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#0f172a",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Video Performance
            </Typography>
            <Box
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: "8px",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b" }}
              >
                {sortedVideos.length} videos
              </Typography>
            </Box>
          </Stack>
        </motion.div>

        {/* Video list */}
        <Box>
          {paginatedVideos.map((vid, index) => {
            const actualRank = (videoPage - 1) * itemsPerPage + index;

            return (
              <VideoRow
                key={vid.id}
                vid={vid}
                percent={
                  (Number(vid.watch_count || 0) / maxWatch) * 100
                }
                rank={actualRank}
                delay={0.1 + index * 0.04}
              />
            );
          })}
        </Box>

        {videoPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 3,
              mb: 4,
            }}
          >
            <Pagination
              count={videoPages}
              page={videoPage}
              onChange={(event, page) => setVideoPage(page)}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        )}
        <Box
          sx={{
            background: "#fff",
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: "#0f172a",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Online Teachers
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: "0.78rem",
                mt: 0.5,
              }}
            >
              Revenue performance for{" "}
              {selectedDate}
            </Typography>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Teacher",
                    "Month Watches",
                    "Month Revenue",
                    "Teacher 20%",
                    "Year Watches",
                    "Year Revenue",
                    "Year 20%",
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        textAlign: "left",
                        padding: "14px 18px",
                        fontSize: "0.72rem",
                        color: "#64748b",
                        background: "#f8fafc",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedTeachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td
                        style={{
                          padding: "16px 18px",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {teacher.name}

                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#94a3b8",
                            marginTop: 3,
                          }}
                        >
                          @{teacher.username}
                        </div>
                      </td>

                      <td>
                        {teacher.month.watches.toLocaleString()}
                      </td>

                      <td>
                        ₦
                        {teacher.month.revenue.toLocaleString()}
                      </td>

                      <td
                        style={{
                          color: "#10b981",
                          fontWeight: 800,
                        }}
                      >
                        ₦
                        {teacher.month.teacher_revenue.toLocaleString()}
                      </td>

                      <td>
                        {teacher.year.watches.toLocaleString()}
                      </td>

                      <td>
                        ₦
                        {teacher.year.revenue.toLocaleString()}
                      </td>

                      <td
                        style={{
                          color: "#10b981",
                          fontWeight: 800,
                        }}
                      >
                        ₦
                        {teacher.year.teacher_revenue.toLocaleString()}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {teacherPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 3,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  <Pagination
                    count={teacherPages}
                    page={teacherPage}
                    onChange={(event, page) => setTeacherPage(page)}
                    color="primary"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              )}
          </Box>
        </Box>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <Typography
            sx={{
              textAlign: "center",
              mt: 5,
              color: "#b0bec5",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            © {new Date().getFullYear()} LiteUpper · Admin Panel
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );
}
